import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { approveParticipationCancellationRequest, fetchParticipationCancellationRequestById } from '../../../controllers/participationCancellationController';
import { DIALOG_MULTILINE_SX, LabeledControl } from '../../groups/EditMemberDialog';
import { filterPaymentLinesForRequest } from '../participationCancellationConstants';
import LogoManualActionsPanel from './LogoManualActionsPanel';
import { formatMoneyWithCurrency } from '../../../utils/currencyDisplay';

const CHECKBOX_SX = {
  color: '#a3a3a3',
  '&.Mui-checked': { color: '#0E4D94' },
};

const FORM_CONTROL_LABEL_SX = {
  ml: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: '13px',
    color: '#444444',
  },
};

const TABLE_HEAD_CELL_SX = {
  fontSize: '14px',
  fontWeight: 500,
  borderBottom: '1px dashed #e8e8e8',
  px: 1.5,
  py: 1.5,
  color: '#a3a3a3',
  whiteSpace: 'nowrap',
};

const TABLE_BODY_CELL_SX = {
  borderBottom: '1px dashed #e8e8e8',
  px: 1.5,
  py: 1.5,
  color: '#444444',
  fontWeight: 500,
  fontSize: '14px',
};

const SECTION_PAPER_SX = {
  p: 2,
  height: '100%',
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
  border: '1px solid #f5f5f5',
  borderRadius: '8px',
};

const SECTION_TITLE_SX = {
  fontSize: '14px',
  fontWeight: 500,
  color: '#444444',
  mb: 1.5,
};

const formatMoney = formatMoneyWithCurrency;

const ApproveParticipationCancellationDialog = ({
  open,
  onClose,
  requestId,
  onApproved,
}) => {
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cancelMemberAttendance, setCancelMemberAttendance] = useState(true);
  const [cancelSpouseAttendance, setCancelSpouseAttendance] = useState(false);
  const [selectedGuestIds, setSelectedGuestIds] = useState([]);
  const [paymentLineStates, setPaymentLineStates] = useState({});
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [logoActionsOpen, setLogoActionsOpen] = useState(false);
  const [logoActions, setLogoActions] = useState([]);

  const requestedGuests = useMemo(
    () => (Array.isArray(detail?.requestedGuests) ? detail.requestedGuests : []),
    [detail],
  );

  const paymentLines = useMemo(
    () => (Array.isArray(detail?.paymentLines) ? detail.paymentLines : []),
    [detail],
  );

  const relevantPaymentLines = useMemo(
    () => filterPaymentLinesForRequest(detail, paymentLines),
    [detail, paymentLines],
  );

  const paidLinesToCancel = useMemo(
    () => relevantPaymentLines.filter((line) => line.isPaid && paymentLineStates[line.guestItemId]),
    [relevantPaymentLines, paymentLineStates],
  );

  useEffect(() => {
    if (!open || !requestId) {
      setDetail(null);
      return;
    }
    let active = true;
    setDetailLoading(true);
    fetchParticipationCancellationRequestById(requestId)
      .then((data) => { if (active) setDetail(data); })
      .catch((e) => {
        if (active) {
          setError(e?.message || 'Detay alınamadı');
          toast.error(e?.message || 'Detay alınamadı');
        }
      })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [open, requestId]);

  useEffect(() => {
    if (!open || !detail) return;
    setError('');
    setSubmitting(false);
    setAdminNotes('');
    setCancelMemberAttendance(true);
    setCancelSpouseAttendance(Boolean(detail.includeSpouse));
    setSelectedGuestIds(requestedGuests.map((g) => g.guestId || g.id).filter(Boolean));

    const initialPaymentStates = {};
    relevantPaymentLines.forEach((line) => {
      if (line.guestItemId) {
        initialPaymentStates[line.guestItemId] = !line.isPaid;
      }
    });
    setPaymentLineStates(initialPaymentStates);
  }, [open, detail, requestedGuests, relevantPaymentLines]);

  const toggleGuest = (guestId) => {
    setSelectedGuestIds((prev) => (
      prev.includes(guestId) ? prev.filter((id) => id !== guestId) : [...prev, guestId]
    ));
  };

  const togglePaymentLine = (guestItemId) => {
    setPaymentLineStates((prev) => ({
      ...prev,
      [guestItemId]: !prev[guestItemId],
    }));
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!detail?.id) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        cancelMemberAttendance,
        cancelSpouseAttendance: detail.includeSpouse ? cancelSpouseAttendance : false,
        guestIdsToCancel: requestedGuests.length > 0
          ? (selectedGuestIds.length > 0 ? selectedGuestIds : null)
          : [],
        paymentLines: relevantPaymentLines.map((line) => ({
          guestItemId: line.guestItemId,
          cancelPayment: Boolean(paymentLineStates[line.guestItemId]),
        })),
        adminNotes: adminNotes?.trim() || null,
      };
      const result = await approveParticipationCancellationRequest(detail.id, payload);
      toast.success(result?.message || 'İptal talebi onaylandı');

      const actions = Array.isArray(result?.logoManualActions) ? result.logoManualActions : [];
      if (actions.length > 0) {
        setLogoActions(actions);
        setLogoActionsOpen(true);
      } else {
        handleClose();
        if (onApproved) onApproved();
      }
    } catch (e) {
      setError(e?.message || 'Onay işlemi başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogoPanelClose = () => {
    setLogoActionsOpen(false);
    setLogoActions([]);
    onClose();
    if (onApproved) onApproved();
  };

  if (!requestId) return null;

  return (
    <>
      <Dialog
        open={open && !logoActionsOpen}
        onClose={(event, reason) => {
          if (submitting) return;
          handleClose();
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          İptal Talebini Onayla
          <IconButton
            type="button"
            onClick={handleClose}
            size="small"
            disabled={submitting}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} sx={{ color: '#0E4D94' }} />
            </Box>
          ) : !detail ? (
            <Alert severity="error">Detay yüklenemedi</Alert>
          ) : (
            <Stack spacing={2.5}>
              {error ? <Alert severity="error">{error}</Alert> : null}
              <Alert severity="info" sx={{ fontSize: '13px', borderRadius: '5px' }}>
                Onay sonrası seçilen katılımlar iptal edilir. Ödeme satırları için ayrı ayrı iptal seçebilirsiniz.
                Ödenmiş satırlarda Logo manuel düzeltme gerekebilir.
              </Alert>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2.5,
                  alignItems: 'stretch',
                }}
              >
                <Box sx={{ flex: { md: '0 0 320px' }, width: { xs: '100%', md: 'auto' } }}>
                  <Paper sx={{ ...SECTION_PAPER_SX, height: '100%' }}>
                    <Typography sx={SECTION_TITLE_SX}>Katılım kapsamı</Typography>
                    <Stack spacing={0.5}>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            checked={cancelMemberAttendance}
                            onChange={(e) => setCancelMemberAttendance(e.target.checked)}
                            disabled={submitting}
                            sx={CHECKBOX_SX}
                          />
                        )}
                        label="Üye katılımını iptal et"
                        sx={FORM_CONTROL_LABEL_SX}
                      />
                      {detail.includeSpouse && (
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={cancelSpouseAttendance}
                              onChange={(e) => setCancelSpouseAttendance(e.target.checked)}
                              disabled={submitting}
                              sx={CHECKBOX_SX}
                            />
                          )}
                          label="Eş katılımını iptal et"
                          sx={FORM_CONTROL_LABEL_SX}
                        />
                      )}
                      {requestedGuests.length > 0 && (
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #e8e8e8' }}>
                          <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, color: '#444444' }}>
                            Misafirler
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#a3a3a3' }}>
                            Boş bırakılırsa tümü iptal edilir
                          </Typography>
                          <Stack spacing={0.25}>
                            {requestedGuests.map((guest) => {
                              const guestId = guest.guestId || guest.id;
                              return (
                                <FormControlLabel
                                  key={guestId}
                                  control={(
                                    <Checkbox
                                      checked={selectedGuestIds.includes(guestId)}
                                      onChange={() => toggleGuest(guestId)}
                                      disabled={submitting}
                                      sx={CHECKBOX_SX}
                                    />
                                  )}
                                  label={guest.fullName || guest.name || guestId}
                                  sx={FORM_CONTROL_LABEL_SX}
                                />
                              );
                            })}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                </Box>

                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                  <Paper sx={{ ...SECTION_PAPER_SX, height: '100%', width: '100%' }}>
                    <Typography sx={SECTION_TITLE_SX}>Ödeme satırları</Typography>
                    {relevantPaymentLines.length === 0 ? (
                      <Typography sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                        Bu talep için ödeme satırı bulunmuyor.
                      </Typography>
                    ) : (
                      <TableContainer
                        sx={{
                          width: '100%',
                          overflowX: 'auto',
                          border: '1px solid #f0f0f0',
                          borderRadius: '5px',
                        }}
                      >
                        <Table size="small" sx={{ minWidth: 640, width: '100%' }}>
                          <TableHead sx={{ bgcolor: 'transparent' }}>
                            <TableRow>
                              <TableCell sx={TABLE_HEAD_CELL_SX}>Kişi</TableCell>
                              <TableCell sx={TABLE_HEAD_CELL_SX}>Tutar</TableCell>
                              <TableCell sx={TABLE_HEAD_CELL_SX}>Ödendi</TableCell>
                              <TableCell sx={TABLE_HEAD_CELL_SX}>Logo borç</TableCell>
                              <TableCell sx={TABLE_HEAD_CELL_SX}>Logo tahsilat</TableCell>
                              <TableCell align="center" sx={TABLE_HEAD_CELL_SX}>İptal et</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {relevantPaymentLines.map((line, index) => (
                              <TableRow
                                key={line.guestItemId}
                                sx={{
                                  '& .MuiTableCell-root': {
                                    ...TABLE_BODY_CELL_SX,
                                    borderBottom: index === relevantPaymentLines.length - 1 ? 'none' : TABLE_BODY_CELL_SX.borderBottom,
                                  },
                                }}
                              >
                                <TableCell>{line.personLabel || '-'}</TableCell>
                                <TableCell>{formatMoney(line.amount)}</TableCell>
                                <TableCell>{line.isPaid ? 'Evet' : 'Hayır'}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                  {line.lastLogoDebtSlipNo || '-'}
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                  {line.collectionExternalReference || '-'}
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={Boolean(paymentLineStates[line.guestItemId])}
                                    onChange={() => togglePaymentLine(line.guestItemId)}
                                    disabled={submitting}
                                    sx={CHECKBOX_SX}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    {paidLinesToCancel.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 2, fontSize: '13px', borderRadius: '5px' }}>
                        {paidLinesToCancel.length} ödenmiş satır iptal edilecek. Logo&apos;da manuel düzeltme gerekebilir.
                      </Alert>
                    )}
                  </Paper>
                </Box>
              </Box>

              <Paper sx={SECTION_PAPER_SX}>
                <LabeledControl label="Admin notları (opsiyonel)">
                  <TextField
                    multiline
                    rows={3}
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    fullWidth
                    disabled={submitting}
                    placeholder=""
                    sx={DIALOG_MULTILINE_SX}
                  />
                </LabeledControl>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Vazgeç
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || detailLoading || !detail}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#17C653',
              borderRadius: '5px',
              px: 2,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#14a847',
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            {submitting ? <CircularProgress size={18} color="inherit" /> : 'Onayla'}
          </Button>
        </DialogActions>
      </Dialog>

      <LogoManualActionsPanel
        open={logoActionsOpen}
        onClose={handleLogoPanelClose}
        actions={logoActions}
      />
    </>
  );
};

export default ApproveParticipationCancellationDialog;
